import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { StreamingAudienceGender, StreamingGender } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { formatShareBp } from '../../periodSections';
import { formatStreamingNumber } from '../../streamingFormat';
import { audienceMessages as messages } from '../../audienceMessages';
import { periodSeriesColor } from '../../period/periodColors';
import { StreamingBlock } from '../../parts/StreamingBlock';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

const GENDER_LABELS: Record<StreamingGender, MessageDescriptor> = {
  female: messages.genderFemale,
  male: messages.genderMale,
  nonBinary: messages.genderNonBinary,
  unknown: messages.genderUnknown,
};

interface AudienceGenderBlockProps {
  gender: readonly StreamingAudienceGender[];
  listeners: number | null;
}

/**
 * Gender (design streaming-sections a1): one bar split by share, then one row per value. A count
 * that the source does not give is a dash; a share is never turned into a count.
 */
const AudienceGenderBlockComponent: React.FC<AudienceGenderBlockProps> = ({ gender, listeners }) => {
  const intl = useIntl();
  const rows = useMemo(
    () =>
      gender.map((row, index) => ({
        ...row,
        color: periodSeriesColor(index),
        name: intl.formatMessage(GENDER_LABELS[row.value]),
        share: formatShareBp(intl, row.shareBp),
      })),
    [gender, intl]
  );

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.gender)}
      meta={
        listeners === null ? undefined : intl.formatMessage(messages.listeners, { count: intl.formatNumber(listeners) })
      }
      testId="streaming-audience-gender"
    >
      <div className={styles.genderBar} aria-hidden>
        {rows.map((row) => (
          <i key={row.value} style={{ width: `${row.shareBp / 100}%`, background: row.color }} />
        ))}
      </div>
      <div className={styles.legendRows}>
        {rows.map((row) => (
          <div key={row.value} className={styles.legendRow}>
            <span className={styles.swatch} style={{ background: row.color }} />
            <span className={styles.legendName}>{row.name}</span>
            <span className={styles.legendShare}>{row.share}</span>
            <span className={styles.legendCount}>
              {row.listeners === null
                ? '—'
                : intl.formatMessage(messages.listeners, { count: formatStreamingNumber(intl, row.listeners) })}
            </span>
          </div>
        ))}
      </div>
    </StreamingBlock>
  );
};

export const AudienceGenderBlock = TypedMemo(AudienceGenderBlockComponent);
